// 組織安全政策（SPEC #org-model-policy、AUTH-04 / AUTH-09）：要求兩步驟驗證、登入時效、閒置逾時、同時工作階段上限、IP 允許清單。
// **只在進入這個組織的資源時套用**：帳號本身與成員的個人組織、其他組織都不受影響；超過時效只對這個組織要求重新驗證，不登出整個帳號。
// 成員都看得到；只有組織擁有者與管理員能改（要 5 分鐘內重新驗證）。存了會把自己擋在外面的（IP 不在清單、要求 MFA 但這次登入沒有）伺服器回 409 WOULD_LOCK_OUT。
// 「事件快照存到雲端」（plan SPEC #events-snapshot-off；platform#71）是獨立開關、切了就存：關掉不用重新驗證、打開要（403 REAUTH_REQUIRED 由 SDK 交給
// StepUpHost 重新驗證後重送）；個人組織沒有這個開關。開關旁寫清楚影響（推播沒縮圖、事件列表 / 預覽格顯示類別圖示、雲端 AI 不能用）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isOrgAdmin, orgApi, type MyOrg } from '../../lib/planeApi';
import { errText } from './OrgSection';

export const IDLE_PRESETS = [15, 30, 60, 240, 480, 720, 1440, 4320, 10080];
export const SESSION_PRESETS: (number | null)[] = [null, 1, 2, 3, 5, 10];
/** 登入時效（分鐘；null = 不限）：超過要重新登入才能用這個組織的設備。 */
export const LOGIN_AGE_PRESETS: (number | null)[] = [null, 60, 480, 720, 1440, 10080, 43200];

export function idleLabel(t: (k: string, o?: Record<string, unknown>) => string, min: number): string {
  if (min % 1440 === 0) return t('policy.days', { count: min / 1440 });
  if (min % 60 === 0) return t('policy.hours', { n: min / 60 });
  return t('policy.minutes', { n: min });
}

export function SecurityPolicyCard({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['org-policy', org.org_id], queryFn: () => orgApi.securityPolicy(org) });
  const [mfa, setMfa] = useState<boolean | null>(null);
  const [age, setAge] = useState<number | null | undefined>(undefined);
  const [idle, setIdle] = useState<number | null>(null);
  const [max, setMax] = useState<number | null | undefined>(undefined);
  const [ips, setIps] = useState<string | null>(null);
  const [keys, setKeys] = useState<boolean | null>(null);
  const [saved, setSaved] = useState(false);
  const admin = isOrgAdmin(org);
  const cur = q.data;
  const mfaV = mfa ?? cur?.require_mfa ?? false;
  const ageV = age === undefined ? (cur?.login_max_age_minutes ?? null) : age;
  const idleV = idle ?? cur?.idle_timeout_minutes ?? 720;
  const maxV = max === undefined ? (cur?.max_sessions ?? null) : max;
  const ipsV = ips ?? (cur?.ip_allowlist ?? []).join('\n');
  const keysV = keys ?? cur?.api_keys === 'allowed';
  const ipList = ipsV
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const save = useMutation({
    mutationFn: () =>
      orgApi.putSecurityPolicy(org, {
        expected_version: cur?.version ?? 0,
        require_mfa: mfaV,
        login_max_age_minutes: ageV,
        idle_timeout_minutes: idleV,
        max_sessions: maxV,
        ip_allowlist: ipList,
        api_keys: keysV ? 'allowed' : 'denied',
      }),
    onSuccess: () => {
      setSaved(true);
      setMfa(null);
      setAge(undefined);
      setIdle(null);
      setMax(undefined);
      setIps(null);
      setKeys(null);
      void qc.invalidateQueries({ queryKey: ['org-policy', org.org_id] });
      void qc.invalidateQueries({ queryKey: ['me-orgs'] });
    },
  });
  const dirty =
    cur &&
    (mfaV !== !!cur.require_mfa ||
      ageV !== (cur.login_max_age_minutes ?? null) ||
      idleV !== cur.idle_timeout_minutes ||
      maxV !== (cur.max_sessions ?? null) ||
      ipList.join(',') !== (cur.ip_allowlist ?? []).join(',') ||
      keysV !== (cur.api_keys === 'allowed'));
  const touched = () => setSaved(false);
  const snapOn = cur?.snapshot_upload !== false;
  const snap = useMutation({
    mutationFn: (on: boolean) => orgApi.putSecurityPolicy(org, { expected_version: cur?.version ?? 0, snapshot_upload: on }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-policy', org.org_id] });
    },
  });
  return (
    <section className="card" data-testid="security-policy">
      <h2 className="section-title">{t('policy.title')}</h2>
      <p className="muted small">{t('policy.hint')}</p>
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {q.isError && <p className="error">{errText(t, q.error)}</p>}
      {cur && (
        <div className="grid-form">
          <label className="check span-2">
            <input type="checkbox" disabled={!admin} checked={mfaV} onChange={(e) => (setMfa(e.target.checked), touched())} data-testid="require-mfa" />
            <span>{t('policy.require_mfa')}</span>
            <span className="small muted">{t('policy.require_mfa_hint')}</span>
          </label>
          <label className="check span-2">
            <input type="checkbox" disabled={!admin} checked={keysV} onChange={(e) => (setKeys(e.target.checked), touched())} data-testid="allow-api-keys" />
            <span>{t('policy.api_keys')}</span>
            <span className="small muted">{t('policy.api_keys_hint')}</span>
          </label>
          {org.kind !== 'personal' && (
            <div className="check span-2" data-testid="snapshot-upload">
              <label className="row-inline">
                <input
                  type="checkbox"
                  disabled={!admin || snap.isPending || dirty === true}
                  checked={snapOn}
                  onChange={(e) => snap.mutate(e.target.checked)}
                  aria-label={t('policy.snapshot_upload')}
                />
                <span>{t('policy.snapshot_upload')}</span>
              </label>
              <span className="small muted">{snapOn ? t('policy.snapshot_upload_hint') : t('policy.snapshot_upload_off_hint')}</span>
              {snap.isError && <span className="error">{errText(t, snap.error)}</span>}
            </div>
          )}
          <label className="field">
            <span>{t('policy.login_max_age')}</span>
            <select
              className="input"
              disabled={!admin}
              value={ageV ?? ''}
              onChange={(e) => (setAge(e.target.value === '' ? null : Number(e.target.value)), touched())}
            >
              {[...new Set([...LOGIN_AGE_PRESETS, cur.login_max_age_minutes ?? null])].map((m) => (
                <option key={String(m)} value={m ?? ''}>
                  {m === null ? t('policy.unlimited') : idleLabel(t, m)}
                </option>
              ))}
            </select>
            <span className="small muted">{t('policy.login_max_age_hint')}</span>
          </label>
          <label className="field">
            <span>{t('policy.idle')}</span>
            <select className="input" disabled={!admin} value={idleV} onChange={(e) => (setIdle(Number(e.target.value)), touched())}>
              {[...new Set([...IDLE_PRESETS, cur.idle_timeout_minutes])]
                .sort((a, b) => a - b)
                .map((m) => (
                  <option key={m} value={m}>
                    {idleLabel(t, m)}
                  </option>
                ))}
            </select>
            <span className="small muted">{t('policy.idle_hint')}</span>
          </label>
          <label className="field">
            <span>{t('policy.max_sessions')}</span>
            <select className="input" disabled={!admin} value={maxV ?? ''} onChange={(e) => (setMax(e.target.value === '' ? null : Number(e.target.value)), touched())}>
              {SESSION_PRESETS.map((n) => (
                <option key={String(n)} value={n ?? ''}>
                  {n === null ? t('policy.unlimited') : t('policy.sessions', { n })}
                </option>
              ))}
            </select>
            <span className="small muted">{t('policy.max_hint')}</span>
          </label>
          <label className="field span-2">
            <span>{t('policy.ip_allowlist')}</span>
            <textarea className="input mono" rows={3} disabled={!admin} value={ipsV} onChange={(e) => (setIps(e.target.value), touched())} placeholder="203.0.113.0/24" />
            <span className="small muted">{t('policy.ip_hint')}</span>
          </label>
          {admin && (
            <div className="row-end span-2">
              {saved && <span className="small muted">{t('policy.saved')}</span>}
              {save.isError && <span className="error">{errText(t, save.error)}</span>}
              <button className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {t('common.save')}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
